import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/apiClient";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export default function Checkout() {
  const { items, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  const [promo, setPromo] = useState("");
  const [applied, setApplied] = useState(null); // { code, discount_amount }
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);

  const discount = applied ? applied.discount_amount : 0;
  const grandTotal = Math.max(0, +(total - discount).toFixed(2));

  const applyPromo = async () => {
    if (!promo.trim()) return;
    setPromoError("");
    setPromoLoading(true);
    try {
      const { data } = await api.post("/discounts/validate", { code: promo.trim(), subtotal: total });
      setApplied({ code: data.code, discount_amount: data.discount_amount });
    } catch (err) {
      setApplied(null);
      setPromoError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    setApplied(null);
    setPromo("");
    setPromoError("");
  };

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  useEffect(() => {
    if (items.length === 0) navigate("/cart");
  }, [items, navigate]);

  useEffect(() => {
    if (applied || total <= 0) return;
    (async () => {
      let code = localStorage.getItem("luxdrop_promo");
      if (!code && localStorage.getItem("luxdrop_ref")) {
        try {
          const { data } = await api.get("/referral/welcome");
          code = data.code;
        } catch { /* no welcome code */ }
      }
      if (!code) {
        try {
          const { data } = await api.get("/featured-promo");
          code = data.code;
        } catch { /* no active promo */ }
      }
      if (!code) return;
      try {
        const { data } = await api.post("/discounts/validate", { code, subtotal: total });
        setApplied({ code: data.code, discount_amount: data.discount_amount });
        setPromo(data.code);
      } catch {
        localStorage.removeItem("luxdrop_promo");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setProcessing(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items,
        email,
        name: name || "Guest",
        discount_code: applied ? applied.code : null,
        ref_code: localStorage.getItem("luxdrop_ref") || null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setProcessing(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="mx-auto max-w-6xl px-5 py-12 md:px-8">
      <h1 className="font-serif text-4xl font-light tracking-tighter text-obsidian md:text-5xl">Checkout</h1>

      <div className="mt-10 grid gap-10 lg:grid-cols-3">
        <form onSubmit={handleSubmit} className="space-y-6 lg:col-span-2">
          <div className="border border-obsidian/15 bg-white p-6">
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-obsidian/50">Contact</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-obsidian/60">Name</label>
                <input
                  data-testid="checkout-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 w-full border border-obsidian/20 bg-bone px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-obsidian focus:ring-offset-1"
                  placeholder="Jane Maker"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-obsidian/60">Email *</label>
                <input
                  data-testid="checkout-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 w-full border border-obsidian/20 bg-bone px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-obsidian focus:ring-offset-1"
                  placeholder="you@email.com"
                />
              </div>
            </div>
          </div>

          <div className="border border-obsidian/15 bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-obsidian/50">Payment</h2>
              <span className="inline-flex items-center gap-1.5 bg-secondary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-obsidian/60">
                <Lock className="h-3 w-3" /> Secured by Stripe
              </span>
            </div>
            <p className="mt-3 flex items-start gap-2 text-sm text-obsidian/60">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-signal" />
              You'll be redirected to Stripe's secure checkout to complete payment. Test card:
              4242 4242 4242 4242, any future expiry, any CVC.
            </p>
          </div>

          {error && <p data-testid="checkout-error" className="text-sm text-signal">{error}</p>}

          <button
            data-testid="place-order-btn"
            type="submit"
            disabled={processing}
            className="w-full bg-signal py-4 text-sm font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-red-700 disabled:opacity-60"
          >
            {processing ? "Redirecting to Stripe…" : `Pay $${grandTotal.toFixed(2)}`}
          </button>
        </form>

        <div>
          <div className="border border-obsidian/15 bg-white p-6">
            <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-obsidian/50">Order summary</h2>
            <div className="mt-5 space-y-3">
              {items.map((it) => (
                <div key={it.product_id} className="flex justify-between text-sm text-obsidian/70">
                  <span className="pr-4">{it.title} × {it.quantity}</span>
                  <span>${(it.price * it.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            {/* Promo code */}
            <div className="mt-5 border-t border-obsidian/10 pt-5">
              {applied ? (
                <div className="flex items-center justify-between bg-secondary px-3 py-2.5">
                  <span className="text-xs font-semibold uppercase tracking-[0.15em] text-obsidian">
                    Code {applied.code} applied
                  </span>
                  <button
                    type="button"
                    data-testid="remove-promo-btn"
                    onClick={removePromo}
                    className="text-obsidian/50 transition-colors hover:text-signal"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    data-testid="promo-input"
                    value={promo}
                    onChange={(e) => setPromo(e.target.value)}
                    placeholder="Promo code"
                    className="min-w-0 flex-1 border border-obsidian/20 bg-bone px-3 py-2.5 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-obsidian focus:ring-offset-1"
                  />
                  <button
                    type="button"
                    data-testid="apply-promo-btn"
                    onClick={applyPromo}
                    disabled={promoLoading}
                    className="shrink-0 border border-obsidian bg-transparent px-4 text-xs font-semibold uppercase tracking-[0.15em] text-obsidian transition-colors hover:bg-obsidian hover:text-white disabled:opacity-60"
                  >
                    {promoLoading ? "…" : "Apply"}
                  </button>
                </div>
              )}
              {promoError && <p data-testid="promo-error" className="mt-2 text-xs text-signal">{promoError}</p>}
            </div>

            <div className="mt-5 space-y-2 border-t border-obsidian/10 pt-5 text-sm text-obsidian/70">
              <div className="flex justify-between"><span>Subtotal</span><span>${total.toFixed(2)}</span></div>
              {discount > 0 && (
                <div className="flex justify-between text-signal" data-testid="discount-line">
                  <span>Discount</span><span>-${discount.toFixed(2)}</span>
                </div>
              )}
            </div>
            <div className="mt-4 flex items-end justify-between border-t border-obsidian/10 pt-4">
              <span className="text-xs uppercase tracking-[0.15em] text-obsidian/50">Total</span>
              <span data-testid="checkout-grand-total" className="font-serif text-3xl tracking-tighter text-obsidian">${grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
